import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches errors thrown during render of a lazy-loaded route — most commonly
 * a chunk-load failure when the deployed index.html points at an asset hash
 * that no longer exists on the server. Without this, React unmounts the entire
 * tree and the user sees a blank/white screen.
 */
export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[RouteErrorBoundary]', error, info.componentStack);
  }

  private isChunkLoadError(): boolean {
    const msg = this.state.error?.message ?? '';
    const name = this.state.error?.name ?? '';
    return (
      name === 'ChunkLoadError' ||
      msg.includes('Loading chunk') ||
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('error loading dynamically imported module')
    );
  }

  private reload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.error) return this.props.children;

    const chunkLoadError = this.isChunkLoadError();
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <div className="max-w-md text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <span className="text-2xl">⚠️</span>
          </div>
          <h2 className="text-lg font-medium text-gray-900">
            {chunkLoadError ? 'This page could not be loaded' : 'Something went wrong'}
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            {chunkLoadError
              ? 'The app has been updated since you opened this tab. Reload to get the latest version.'
              : 'An unexpected error occurred while rendering this page. Try reloading. If it keeps happening, contact support.'}
          </p>
          <details className="mt-3 text-left text-xs text-gray-400">
            <summary className="cursor-pointer">Technical details</summary>
            <pre className="mt-1 overflow-auto whitespace-pre-wrap break-words rounded bg-gray-50 p-2">
              {this.state.error.name}: {this.state.error.message}
            </pre>
          </details>
          <button
            type="button"
            onClick={this.reload}
            className="mt-4 inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
